import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClientTrail, { tabCrumbs, clientTabPath } from '../components/ClientTrail';
import { supabase, addDocument, createNurseAssessmentVersion } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { downloadAssessmentDoc } from '../lib/generateAssessmentDoc';
import { listCycles, loadCycleFacts, physicianOrderOnFile } from '../lib/paCycles';
import { CLIENT_DRIVE_ROOT } from '../lib/driveRoots';

// ─── AUTOSAVE PERSISTENCE ─────────────────────────────────────
// Content fields we persist (DB column names). Meta fields (client_id,
// completed_by, updated_at) are written separately on every save.
const CONTENT_KEYS = [
  'assessment_date','primary_diagnosis','secondary_diagnoses','hospitalizations','status',
  'equipment','other_equipment','equipment_notes',
  'feeding_method','feeding_tolerance','feeding_notes',
  'dependency_level','adl_support_needed','functional_notes',
  'respiratory_pattern','airway_needs','respiratory_notes',
  'neuro_diagnoses','neuro_notes',
  'skin_integrity','gu_status','skin_notes',
  'behavioral_flags','behavioral_notes',
  'caregiver_ability','caregiver_availability','caregiver_notes',
  'med_necessity_narrative','skilled_needs_summary','unskilled_needs_summary',
  'skilled_hours_justification','unskilled_hours_justification',
  'skilled_interventions','skilled_goals','adl_unskilled','supervision_needs','unskilled_goals',
  'skilled_hours_requested','unskilled_hours_requested','hours_justification',
  'safety_risks','preventive_strategies','emergency_protocol',
  'competency_audit','teaching_plan','iep_provided',
];
const lsKey = (cid) => `na_form_${cid}`;
// Pick just the persisted content fields off a DB row / payload object.
const pickContent = (row) => {
  const out = {};
  for (const k of CONTENT_KEYS) out[k] = row?.[k];
  return out;
};

// ─── THEME ────────────────────────────────────────────────────
const NAVY   = 'var(--accent)';
const STEEL  = 'var(--accent)';
const LIGHT  = 'var(--accent-bg)';
const GREEN  = 'var(--success)';
const AMBER  = 'var(--warn)';
// Section/verify-card headers used to get RED/TEAL/PURPLE per section purely
// as a category identifier (Emergency Contact vs PCP vs Ordering Physician) —
// no urgency difference between them, so all default to NAVY like every other
// section header (see CAERA_DASHBOARD_REDESIGN_SPEC.md §9).
const RED    = NAVY;
const TEAL   = NAVY;
const PURPLE = NAVY;
const BORDER = 'var(--accent-border)';

// ─── STYLE HELPERS ────────────────────────────────────────────
const card = {
  background: '#fff', borderRadius: 10, border: `1px solid ${BORDER}`,
  marginBottom: 28, overflow: 'hidden', boxShadow: '0 1px 6px rgba(26,58,107,0.07)'
};
const mkHeader = (color = NAVY) => ({
  background: color, color: '#fff', padding: '13px 22px',
  fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700,
  display: 'flex', alignItems: 'center', gap: 10
});
const subHdr = {
  background: LIGHT, color: NAVY, padding: '9px 22px',
  fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700,
  borderBottom: `1px solid ${BORDER}`, borderTop: `1px solid ${BORDER}`
};
const lbl = {
  fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12,
  fontWeight: 700, color: NAVY, marginBottom: 4, display: 'block'
};
const inp = {
  width: '100%', border: `1.5px solid ${BORDER}`, borderRadius: 6,
  padding: '8px 11px', fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13,
  color: 'var(--text)', background: 'var(--bg2)', boxSizing: 'border-box', outline: 'none'
};
const txa = { ...inp, minHeight: 80, resize: 'vertical' };
const cbRow = { display: 'flex', flexWrap: 'wrap', gap: '8px 20px', padding: '10px 0' };
const pad = { padding: '18px 22px' };

// ─── PRIMITIVES ───────────────────────────────────────────────
const F = ({ label, children, half, third }) => (
  <div style={{ flex: third ? '1 1 30%' : half ? '1 1 45%' : '1 1 100%', minWidth: 150, marginBottom: 14 }}>
    {label && <label style={lbl}>{label}</label>}
    {children}
  </div>
);
const TI = ({ value, onChange, placeholder, type = 'text', readOnly }) => (
  <input type={type} style={{ ...inp, background: readOnly ? 'var(--bg2)' : 'var(--card-bg)', cursor: readOnly ? 'not-allowed' : 'text' }}
    value={value || ''} onChange={e => !readOnly && onChange(e.target.value)}
    placeholder={placeholder || ''} readOnly={readOnly} />
);
const TA = ({ value, onChange, placeholder, rows }) => (
  <textarea style={{ ...txa, minHeight: rows ? rows * 22 : 80 }}
    value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />
);
const Radio = ({ options, value, onChange }) => (
  <div style={cbRow}>
    {options.map(o => (
      <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif", cursor: 'pointer' }}>
        <input type="radio" checked={value === o} onChange={() => onChange(o)} style={{ accentColor: STEEL }} />{o}
      </label>
    ))}
  </div>
);
const Checks = ({ options, value, onChange }) => (
  <div style={cbRow}>
    {options.map(o => (
      <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif", cursor: 'pointer' }}>
        <input type="checkbox" checked={(value || []).includes(o)}
          onChange={() => onChange((value || []).includes(o) ? (value || []).filter(v => v !== o) : [...(value || []), o])}
          style={{ accentColor: STEEL }} />{o}
      </label>
    ))}
  </div>
);
const Toggle = ({ label, value, onChange, tc = GREEN }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontFamily: "'Trebuchet MS', sans-serif" }}>
    <div onClick={() => onChange(!value)}
      style={{ width: 42, height: 24, borderRadius: 12, background: value ? tc : 'var(--border2)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
    <span style={{ fontWeight: value ? 700 : 400, color: value ? tc : 'var(--text3)' }}>{label}</span>
  </label>
);
const SHdr = ({ n, title, color }) => (
  <div style={mkHeader(color)}>
    <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, flexShrink: 0 }}>{n}</span>
    {title}
  </div>
);
const SubH = ({ title }) => <div style={subHdr}>{title}</div>;

// ─── VERIFY CARD ──────────────────────────────────────────────
const VerifyCard = ({ title, icon, color, fields, data, onChange, note }) => (
  <div style={{ border: `1.5px solid ${color}30`, borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
    <div style={{ background: `${color}10`, borderBottom: `1px solid ${color}25`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: NAVY }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{note || 'Pre-filled from patient profile — verify and correct as needed'}</div>
      </div>
      <div style={{ background: `${color}20`, border: `1px solid ${color}40`, borderRadius: 5, padding: '3px 9px', fontSize: 10, fontWeight: 700, color, whiteSpace: 'nowrap' }}>VERIFY</div>
    </div>
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(({ key, label, placeholder, half, third, type, readOnly }) => (
          <div key={key} style={{ flex: third ? '1 1 30%' : half ? '1 1 45%' : '1 1 100%', minWidth: 150 }}>
            <label style={lbl}>{label}</label>
            <TI type={type || 'text'} value={data[key]} onChange={v => onChange(key, v)}
              placeholder={placeholder || ''} readOnly={readOnly} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── TABLE SECTION ────────────────────────────────────────────
const TableSec = ({ columns, data, onChange, addRow }) => (
  <div style={{ overflowX: 'auto', padding: '0 22px 16px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead><tr>{columns.map(c => (
        <th key={c} style={{ background: LIGHT, color: NAVY, padding: '8px 10px', border: `1px solid ${BORDER}`, fontWeight: 700, textAlign: 'left', fontSize: 11 }}>{c}</th>
      ))}</tr></thead>
      <tbody>{data.map((row, i) => (
        <tr key={i}>{columns.map((c, j) => (
          <td key={c} style={{ border: `1px solid ${BORDER}`, padding: 4 }}>
            <textarea value={row[j] || ''} onChange={e => onChange(i, j, e.target.value)}
              style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, resize: 'vertical', minHeight: 50, outline: 'none', padding: 4, boxSizing: 'border-box', fontFamily: "'Trebuchet MS', sans-serif" }} />
          </td>
        ))}</tr>
      ))}</tbody>
    </table>
    <button onClick={addRow} style={{ marginTop: 8, background: 'none', border: `1px dashed ${STEEL}`, borderRadius: 5, color: STEEL, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif" }}>+ Add Row</button>
  </div>
);

const ProgBar = ({ step, total }) => (
  <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: i < step ? STEEL : BORDER, transition: 'background 0.3s' }} />
    ))}
  </div>
);

const emptyRows = (n, cols = 4) => Array.from({ length: n }, () => Array(cols).fill(''));

const notice = (color, bg, icon, text) => (
  <div style={{ background: bg, border: `1px solid ${color}`, borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 11, color, display: 'flex', gap: 8 }}>
    <span>{icon}</span><span dangerouslySetInnerHTML={{ __html: text }} />
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function NurseAssessmentForm() {
  const { id } = useParams();           // client_id from URL e.g. G001
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saved' | 'error'
  const [saveError, setSaveError]     = useState('');
  const [activeSection, setActiveSection] = useState(0);
  const [clientDbId, setClientDbId] = useState(null); // internal UUID
  const [clientStrId, setClientStrId] = useState('');   // e.g. G001
  const [formStatus, setFormStatus] = useState('draft'); // 'draft' | 'complete'

  // ── Tab 0: Patient Info (editable, pre-filled) ──────────────
  const [naDate, setNaDate]           = useState('');
  const [iepProvided, setIepProvided] = useState('');

  const [patientInfo, setPatientInfo] = useState({
    firstName: '', lastName: '', dob: '', age: '', gender: '',
    language: '', address: '', city: '', county: '', state: 'GA', zip: '', medicaidId: ''
  });
  const updPatient = (k, v) => setPatientInfo(p => ({ ...p, [k]: v }));

  const [guardian, setGuardian] = useState({
    firstName: '', lastName: '', relationship: '', phone: '', altPhone: '', email: '', livesWithChild: ''
  });
  const updGuardian = (k, v) => setGuardian(p => ({ ...p, [k]: v }));

  const [emergency, setEmergency] = useState({
    firstName: '', lastName: '', relationship: '', phone: '', altPhone: '', email: ''
  });
  const updEmergency = (k, v) => setEmergency(p => ({ ...p, [k]: v }));

  const [pcp, setPcp] = useState({ name: '', practice: '', address: '', phone: '', fax: '', npi: '' });
  const updPcp = (k, v) => setPcp(p => ({ ...p, [k]: v }));

  const [op, setOp] = useState({ name: '', specialty: '', practice: '', address: '', phone: '', fax: '', npi: '' });
  const updOp = (k, v) => setOp(p => ({ ...p, [k]: v }));

  // ── Tab 1: Clinical flags & vitals ──────────────────────────
  const [primaryDx, setPrimaryDx]         = useState('');
  const [secondaryDx, setSecondaryDx]     = useState('');
  const [weight, setWeight]               = useState('');
  const [diet, setDiet]                   = useState('');
  const [allergies, setAllergies]         = useState('');
  const [hasAllergies, setHasAllergies]   = useState(false);
  const [medications, setMedications]     = useState('');
  const [hospitalizations, setHosp]       = useState('');
  const [seizures, setSeizures]           = useState(false);
  const [gTube, setGTube]                 = useState(false);
  const [usesOxygen, setUsesOxygen]       = useState(false);
  const [pottyTrained, setPottyTrained]   = useState(false);
  const [wearsDiapers, setWearsDiapers]   = useState(false);
  const [contractures, setContractures]   = useState(false);
  const [swallowing, setSwallowing]       = useState(false);
  const [equipment, setEquipment]         = useState([]);
  const [otherEquip, setOtherEquip]       = useState('');
  const [equipNotes, setEquipNotes]       = useState('');
  const [feedingMethod, setFeedingMethod] = useState([]);
  const [feedingTol, setFeedingTol]       = useState('');
  const [feedingNotes, setFeedingNotes]   = useState('');
  const [depLevel, setDepLevel]           = useState('');
  const [adlSupport, setAdlSupport]       = useState([]);
  const [funcNotes, setFuncNotes]         = useState('');
  const [respPattern, setRespPattern]     = useState('');
  const [airwayNeeds, setAirwayNeeds]     = useState([]);
  const [respNotes, setRespNotes]         = useState('');
  const [neuroDx, setNeuroDx]             = useState([]);
  const [neuroNotes, setNeuroNotes]       = useState('');
  const [skinIntegrity, setSkinIntegrity] = useState('');
  const [guStatus, setGuStatus]           = useState('');
  const [skinNotes, setSkinNotes]         = useState('');
  const [behavFlags, setBehavFlags]       = useState([]);
  const [behavNotes, setBehavNotes]       = useState('');
  const [cgAbility, setCgAbility]         = useState('');
  const [cgAvail, setCgAvail]             = useState('');
  const [cgNotes, setCgNotes]             = useState('');
  const [medNarrative, setMedNarrative]   = useState('');
  const [skilledNeeds, setSkilledNeeds]   = useState('');
  const [unskilledNeeds, setUnskilledNeeds] = useState('');

  // ── Tab 2: Care Plan ─────────────────────────────────────────
  const [skilledInterv, setSkilledInterv]   = useState([]);
  const [skilledTable, setSkilledTable]     = useState(emptyRows(5));
  const [adlSupport2, setAdlSupport2]       = useState([]);
  const [supervNeeds, setSupervNeeds]       = useState([]);
  const [unskilledTable, setUnskilledTable] = useState(emptyRows(5));
  const [skilledHrs, setSkilledHrs]         = useState('');
  const [unskilledHrs, setUnskilledHrs]     = useState('');
  const [hrsJustification, setHrsJust]      = useState('');
  const [safetyRisks, setSafetyRisks]       = useState([]);
  const [preventive, setPreventive]         = useState('');
  const [emergProtocol, setEmergProtocol]   = useState('');

  // ── Tab 3: Caregiver Training ────────────────────────────────
  const [competencyTable, setCompetencyTable] = useState(emptyRows(5));
  const [teachingTable, setTeachingTable]     = useState(emptyRows(5));

  // ── Autosave ─────────────────────────────────────────────────
  const [autoSaveAt, setAutoSaveAt]   = useState(null); // Date | null
  const [autoSaving, setAutoSaving]   = useState(false);
  const [loadError, setLoadError]     = useState('');   // set when the saved row fails to load
  const loadedRef      = useRef(false);
  const loadOkRef      = useRef(false);  // true only after a clean load — gates autosave
  const baselineRef    = useRef({});     // last-known-saved field values, for diffing
  const existingIdRef  = useRef(null);   // id of the existing nurse_assessments row, if any
  const autoSaveTimer  = useRef(null);
  const localMirrorTimer = useRef(null);
  const retryTimer     = useRef(null);
  const lastVersionAtRef = useRef(0); // throttle version snapshots during autosave

  // ── AI Narrative states ─────────────────────────────────────
  const [generating, setGenerating]             = useState(false);
  const [narrativeGenerated, setNarrativeGenerated] = useState(false);
  // Section notes (1.2 – 1.9)
  const [aiEquipNotes, setAiEquipNotes]         = useState('');
  const [aiFeedingNotes, setAiFeedingNotes]     = useState('');
  const [aiFuncNotes, setAiFuncNotes]           = useState('');
  const [aiRespNotes, setAiRespNotes]           = useState('');
  const [aiNeuroNotes, setAiNeuroNotes]         = useState('');
  const [aiSkinNotes, setAiSkinNotes]           = useState('');
  const [aiBehavNotes, setAiBehavNotes]         = useState('');
  const [aiCgNotes, setAiCgNotes]               = useState('');
  // Section 2 content
  const [aiHrsJust, setAiHrsJust]               = useState('');
  const [aiPreventive, setAiPreventive]         = useState('');
  // Tables
  const [aiSkilledGoals, setAiSkilledGoals]         = useState(emptyRows(5));
  const [aiUnskilledGoals, setAiUnskilledGoals]     = useState(emptyRows(5));
  const [aiCompetencyAudit, setAiCompetencyAudit]   = useState(emptyRows(5));
  const [aiTeachingPlan, setAiTeachingPlan]         = useState(emptyRows(5));
  // Narratives
  const [aiMedNarrative, setAiMedNarrative]     = useState('');
  const [aiSkilledJust, setAiSkilledJust]       = useState('');
  const [aiUnskilledJust, setAiUnskilledJust]   = useState('');
  const [aiEmergProtocol, setAiEmergProtocol]   = useState('');
  const [generateError, setGenerateError]       = useState('');

  const updTable = setter => (i, j, val) =>
    setter(prev => prev.map((row, ri) => ri === i ? row.map((cell, ci) => ci === j ? val : cell) : row));
  const addRow = (setter, cols = 4) => () => setter(prev => [...prev, Array(cols).fill('')]);

  // ─── PRE-FILL FROM SUPABASE ───────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const fetchClient = async () => {
      setLoading(true);
      const { data: client, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !client) {
        console.error('Failed to load client:', error);
        setLoadError('Could not load this patient record. Please reload the page before entering any data.');
        setLoading(false);
        return;
      }

      setClientDbId(client.id);
      setClientStrId(client.client_id);

      // Pre-fill patient info
      setPatientInfo({
        firstName:  client.first_name  || '',
        lastName:   client.last_name   || '',
        dob:        client.date_of_birth || '',
        age:        client.date_of_birth
                      ? String(Math.floor((Date.now() - new Date(client.date_of_birth)) / 31557600000))
                      : '',
        gender:     client.gender      || '',
        language:   client.language    || '',
        address:    client.address     || '',
        city:       client.city        || '',
        county:     client.county      || '',
        state:      client.state       || 'GA',
        zip:        client.zip         || '',
        medicaidId: client.medicaid_id || '',
      });

      // Pre-fill guardian
      setGuardian({
        firstName:      client.guardian_first_name   || '',
        lastName:       client.guardian_last_name     || '',
        relationship:   client.guardian_relationship || '',
        phone:          client.guardian_phone        || '',
        altPhone:       client.guardian_alt_phone    || '',
        email:          client.guardian_email        || '',
        livesWithChild: client.guardian_lives_with_child || '',
      });

      // Pre-fill emergency contact
      setEmergency({
        firstName:    client.emergency_first_name   || '',
        lastName:     client.emergency_last_name     || '',
        relationship: client.emergency_relationship || '',
        phone:        client.emergency_phone        || '',
        altPhone:     client.emergency_alt_phone    || '',
        email:        client.emergency_email        || '',
      });

      // Pre-fill PCP
      setPcp({
        name:     client.doctor_name     || '',
        practice: client.doctor_practice || '',
        address:  client.doctor_address  || '',
        phone:    client.doctor_phone    || '',
        fax:      client.doctor_fax      || '',
        npi:      client.doctor_npi      || '',
      });

      // Pre-fill ordering physician
      setOp({
        name:      client.ordering_physician_name      || '',
        specialty: client.ordering_physician_specialty || '',
        practice:  client.ordering_physician_practice  || '',
        address:   client.ordering_physician_address   || '',
        phone:     client.ordering_physician_phone     || '',
        fax:       client.ordering_physician_fax       || '',
        npi:       client.ordering_physician_npi       || '',
      });

      // Pre-fill clinical fields
      const dxParts = (client.full_diagnoses || '').split(' | ');
      setPrimaryDx(dxParts[0] || '');
      setSecondaryDx(dxParts.slice(1).join(' | ') || '');
      setWeight(client.weight_lbs ? String(client.weight_lbs) : '');
      setDiet(client.diet_description || '');
      setAllergies(client.allergies || '');
      setHasAllergies(!!client.has_allergies);
      setMedications(client.current_medications || '');
      setHosp(client.hospitalizations || '');

      // Pre-fill medical flags
      setSeizures(!!client.seizures);
      setGTube(!!client.g_tube);
      setUsesOxygen(!!client.uses_oxygen);
      setPottyTrained(!!client.potty_trained);
      setWearsDiapers(!!client.diapers);
      setContractures(!!client.contractures);
      setSwallowing(!!client.swallowing_issues);

      // Pre-fill equipment from existing data
      if (client.medical_equipment && Array.isArray(client.medical_equipment)) {
        const otherItem = client.medical_equipment.find((e: string) => e.startsWith('Other:'));
        if (otherItem) setOtherEquip(otherItem.replace(/^Other:\s*/, ''));
        setEquipment(client.medical_equipment.filter((e: string) => !e.startsWith('Other:')));
      }

      // Pre-fill assessment date if exists
      if (client.nurse_assessment_date) setNaDate(client.nurse_assessment_date);

      // Load existing nurse assessment if it exists.
      // maybeSingle (not single) so "no row yet" is data:null with NO error —
      // this lets us tell a brand-new assessment apart from a failed read.
      const { data: existing, error: naErr } = await supabase
        .from('nurse_assessments')
        .select('*')
        .eq('client_id', client.client_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (naErr) {
        // The read FAILED while a row may well exist. Do NOT enable autosave,
        // or a blank form could overwrite the saved record. Block editing and
        // tell the nurse to reload.
        console.error('Failed to load existing assessment:', naErr);
        setLoadError('Could not load the saved assessment. Please reload the page before entering any data — this prevents overwriting saved work.');
        setLoading(false);
        return;
      }

      if (existing) {
        applyExisting(existing);
        existingIdRef.current = existing.id;
      }

      // Baseline = what is currently saved on the server. Autosave diffs against
      // this and only writes fields that actually changed, so a field the nurse
      // never touched can never be blanked out.
      baselineRef.current = existing ? pickContent(existing) : {};

      // Device backup: if this browser holds newer unsynced changes from a prior
      // session (server save failed, session expired, tab closed), offer to
      // restore them so nothing is lost.
      try {
        const raw = localStorage.getItem(lsKey(client.client_id));
        if (raw) {
          const local = JSON.parse(raw);
          const localTime  = new Date(local._savedAt || 0).getTime();
          const serverTime = new Date(existing?.updated_at || 0).getTime();
          if (localTime > serverTime + 1000) {
            const when = new Date(local._savedAt).toLocaleString();
            // eslint-disable-next-line no-alert
            if (window.confirm(`Unsaved changes from this device were found (last edited ${when}) that are newer than the saved copy. Restore them?`)) {
              applyExisting(local); // stored in DB-column shape, so the same mapper works
            } else {
              localStorage.removeItem(lsKey(client.client_id));
            }
          }
        }
      } catch (_) { /* ignore corrupt/full storage */ }

      // Mark as loaded after a brief delay so the initial state-sets don't
      // trigger autosave. loadOkRef gates ALL server writes from here on.
      setTimeout(() => { loadedRef.current = true; loadOkRef.current = true; }, 500);
      setLoading(false);
    };

    fetchClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Populate the form from a saved row OR a device-backup payload (both use
  // DB-column names). Hoisted via function declaration so the load effect above
  // can call it. (Moved verbatim from the previous inline load mapping.)
  function applyExisting(existing) {
    if (existing.iep_provided) setIepProvided(existing.iep_provided);
    if (existing.status) setFormStatus(existing.status);
    if (existing.assessment_date) setNaDate(existing.assessment_date);
    setFeedingMethod(existing.feeding_method || []);
    setFeedingTol(existing.feeding_tolerance || '');
    setFeedingNotes(existing.feeding_notes || '');
    setDepLevel(existing.dependency_level || '');
    setAdlSupport(existing.adl_support_needed || []);
    setFuncNotes(existing.functional_notes || '');
    setRespPattern(existing.respiratory_pattern || '');
    setAirwayNeeds(existing.airway_needs || []);
    setRespNotes(existing.respiratory_notes || '');
    setNeuroDx(existing.neuro_diagnoses || []);
    setNeuroNotes(existing.neuro_notes || '');
    setSkinIntegrity(existing.skin_integrity || '');
    setGuStatus(existing.gu_status || '');
    setSkinNotes(existing.skin_notes || '');
    setBehavFlags(existing.behavioral_flags || []);
    setBehavNotes(existing.behavioral_notes || '');
    setCgAbility(existing.caregiver_ability || '');
    setCgAvail(existing.caregiver_availability || '');
    setCgNotes(existing.caregiver_notes || '');
    setMedNarrative(existing.med_necessity_narrative || '');
    setSkilledNeeds(existing.skilled_needs_summary || '');
    setUnskilledNeeds(existing.unskilled_needs_summary || '');
    // These two are now persisted columns, so hydrate them unconditionally —
    // not just when a prior generation is detected. If they stayed at '' while
    // the saved row held text, the autosave diff would blank the column.
    setAiSkilledJust(existing.skilled_hours_justification || existing.skilled_needs_summary || '');
    setAiUnskilledJust(existing.unskilled_hours_justification || existing.unskilled_needs_summary || '');
    setSkilledInterv(existing.skilled_interventions || []);
    setAdlSupport2(existing.adl_unskilled || []);
    setSupervNeeds(existing.supervision_needs || []);
    setSkilledHrs(existing.skilled_hours_requested ? String(existing.skilled_hours_requested) : '');
    setUnskilledHrs(existing.unskilled_hours_requested ? String(existing.unskilled_hours_requested) : '');
    setHrsJust(existing.hours_justification || '');
    setSafetyRisks(existing.safety_risks || []);
    setPreventive(existing.preventive_strategies || '');
    setEmergProtocol(existing.emergency_protocol || '');
    if (existing.equipment_notes) setEquipNotes(existing.equipment_notes);
    if (existing.skilled_goals) setSkilledTable(existing.skilled_goals);
    if (existing.unskilled_goals) setUnskilledTable(existing.unskilled_goals);
    if (existing.competency_audit) setCompetencyTable(existing.competency_audit);
    if (existing.teaching_plan) setTeachingTable(existing.teaching_plan);

    // Restore the generated-document state so reopening shows the confirm /
    // generate section with its prior content — instead of hiding it and
    // forcing a fresh AI run that would lose the work.
    const hadPriorGeneration = !!(
      existing.drive_document_url || existing.med_necessity_narrative ||
      existing.hours_justification || existing.ai_narrative_generated ||
      existing.skilled_hours_justification || existing.unskilled_hours_justification ||
      (existing.teaching_plan && existing.teaching_plan.length) ||
      (existing.competency_audit && existing.competency_audit.length)
    );
    if (hadPriorGeneration) {
      setNarrativeGenerated(true);
      setAiEquipNotes(existing.equipment_notes || '');
      setAiFeedingNotes(existing.feeding_notes || '');
      setAiFuncNotes(existing.functional_notes || '');
      setAiRespNotes(existing.respiratory_notes || '');
      setAiNeuroNotes(existing.neuro_notes || '');
      setAiSkinNotes(existing.skin_notes || '');
      setAiBehavNotes(existing.behavioral_notes || '');
      setAiCgNotes(existing.caregiver_notes || '');
      setAiMedNarrative(existing.med_necessity_narrative || '');
      setAiHrsJust(existing.hours_justification || '');
      setAiPreventive(existing.preventive_strategies || '');
      setAiEmergProtocol(existing.emergency_protocol || '');
      if (existing.skilled_goals)    setAiSkilledGoals(existing.skilled_goals);
      if (existing.unskilled_goals)  setAiUnskilledGoals(existing.unskilled_goals);
      if (existing.competency_audit) setAiCompetencyAudit(existing.competency_audit);
      if (existing.teaching_plan)    setAiTeachingPlan(existing.teaching_plan);
    }
    if (existing.drive_document_url) {
      setDocDriveUrl(existing.drive_document_url);
      setDocVersion(existing.version_number || null);
      setDocVersionDate(existing.version_created_at || '');
    }
    // Extract "Other:" from equipment array
    if (existing.equipment && Array.isArray(existing.equipment)) {
      const otherItem = existing.equipment.find((e) => e.startsWith('Other:'));
      if (otherItem) setOtherEquip(otherItem.replace(/^Other:\s*/, ''));
      setEquipment(existing.equipment.filter((e) => !e.startsWith('Other:')));
    }
  }

  // ─── AUTOSAVE ─────────────────────────────────────────────────
  // Snapshot of all nurse_assessments fields — changes trigger the debounce
  const formSnapshot = useMemo(() => JSON.stringify({
    naDate, primaryDx, secondaryDx, hospitalizations, formStatus,
    equipment, otherEquip, equipNotes,
    feedingMethod, feedingTol, feedingNotes,
    depLevel, adlSupport, funcNotes,
    respPattern, airwayNeeds, respNotes,
    neuroDx, neuroNotes,
    skinIntegrity, guStatus, skinNotes,
    behavFlags, behavNotes,
    cgAbility, cgAvail, cgNotes,
    medNarrative, skilledNeeds, unskilledNeeds,
    aiSkilledJust, aiUnskilledJust,
    skilledInterv, skilledTable, adlSupport2, supervNeeds, unskilledTable,
    skilledHrs, unskilledHrs, hrsJustification,
    safetyRisks, preventive, emergProtocol,
    competencyTable, teachingTable,
  }), [naDate, primaryDx, secondaryDx, hospitalizations, formStatus,
    equipment, otherEquip, equipNotes,
    feedingMethod, feedingTol, feedingNotes,
    depLevel, adlSupport, funcNotes,
    respPattern, airwayNeeds, respNotes,
    neuroDx, neuroNotes,
    skinIntegrity, guStatus, skinNotes,
    behavFlags, behavNotes,
    cgAbility, cgAvail, cgNotes,
    medNarrative, skilledNeeds, unskilledNeeds,
    aiSkilledJust, aiUnskilledJust,
    skilledInterv, skilledTable, adlSupport2, supervNeeds, unskilledTable,
    skilledHrs, unskilledHrs, hrsJustification,
    safetyRisks, preventive, emergProtocol,
    competencyTable, teachingTable,
  ]);

  // Single source of truth for the saved record (DB-column shape).
  const buildPayload = useCallback(() => ({
    client_id:              clientStrId,
    client_uuid:            clientDbId,
    assessment_date:        naDate || new Date().toISOString().split('T')[0],
    completed_by:           profile?.id || null,
    completed_by_name:      profile?.full_name || null,
    status:                 formStatus,
    primary_diagnosis:      primaryDx,
    secondary_diagnoses:    secondaryDx,
    hospitalizations,
    equipment:              [...equipment, ...(otherEquip ? [`Other: ${otherEquip}`] : [])],
    other_equipment:        otherEquip,
    equipment_notes:        equipNotes,
    feeding_method:         feedingMethod,
    feeding_tolerance:      feedingTol,
    feeding_notes:          feedingNotes,
    dependency_level:       depLevel,
    adl_support_needed:     adlSupport,
    functional_notes:       funcNotes,
    respiratory_pattern:    respPattern,
    airway_needs:           airwayNeeds,
    respiratory_notes:      respNotes,
    neuro_diagnoses:        neuroDx,
    neuro_notes:            neuroNotes,
    skin_integrity:         skinIntegrity,
    gu_status:              guStatus,
    skin_notes:             skinNotes,
    behavioral_flags:       behavFlags,
    behavioral_notes:       behavNotes,
    caregiver_ability:      cgAbility,
    caregiver_availability: cgAvail,
    caregiver_notes:        cgNotes,
    med_necessity_narrative: medNarrative,
    skilled_needs_summary:  skilledNeeds,
    unskilled_needs_summary: unskilledNeeds,
    // AI-generated needs narratives — these render as "Identified Skilled /
    // Unskilled Needs" in the .docx and must survive a reload like every other
    // generated section.
    skilled_hours_justification:   aiSkilledJust,
    unskilled_hours_justification: aiUnskilledJust,
    skilled_interventions:  skilledInterv,
    skilled_goals:          skilledTable,
    adl_unskilled:          adlSupport2,
    supervision_needs:      supervNeeds,
    unskilled_goals:        unskilledTable,
    skilled_hours_requested:  skilledHrs ? Number(skilledHrs) : null,
    unskilled_hours_requested: unskilledHrs ? Number(unskilledHrs) : null,
    hours_justification:    hrsJustification,
    safety_risks:           safetyRisks,
    preventive_strategies:  preventive,
    emergency_protocol:     emergProtocol,
    competency_audit:       competencyTable,
    teaching_plan:          teachingTable,
    iep_provided:           iepProvided || null,
    updated_at:             new Date().toISOString(),
  }), [clientStrId, clientDbId, profile, naDate, primaryDx, secondaryDx, hospitalizations, formStatus,
    equipment, otherEquip, equipNotes, feedingMethod, feedingTol, feedingNotes,
    depLevel, adlSupport, funcNotes, respPattern, airwayNeeds, respNotes,
    neuroDx, neuroNotes, skinIntegrity, guStatus, skinNotes, behavFlags, behavNotes,
    cgAbility, cgAvail, cgNotes, medNarrative, skilledNeeds, unskilledNeeds,
    aiSkilledJust, aiUnskilledJust,
    skilledInterv, skilledTable, adlSupport2, supervNeeds, unskilledTable,
    skilledHrs, unskilledHrs, hrsJustification, safetyRisks, preventive, emergProtocol,
    competencyTable, teachingTable, iepProvided,
  ]);

  // Write the current form to this device's storage. Synchronous, can't fail
  // server-side — this is the net that means nurses never lose work, even if
  // the server is unreachable or the session expires.
  const mirrorToDevice = useCallback(() => {
    if (!clientStrId || !loadOkRef.current) return;
    try {
      localStorage.setItem(lsKey(clientStrId), JSON.stringify({ ...buildPayload(), _savedAt: new Date().toISOString() }));
    } catch (_) { /* storage full/unavailable */ }
  }, [clientStrId, buildPayload]);

  const doAutoSave = useCallback(async () => {
    // Gate: never write unless the saved record loaded cleanly. This is what
    // stops a blank/half-loaded form from overwriting good data.
    if (!clientStrId || !loadedRef.current || !loadOkRef.current) return;

    const payload = buildPayload();
    mirrorToDevice();

    // Diff against the last-saved baseline — only persist fields that actually
    // changed, so a field the nurse never touched can never be blanked.
    const base = baselineRef.current || {};
    const changed = {};
    for (const k of CONTENT_KEYS) {
      if (JSON.stringify(payload[k]) !== JSON.stringify(base[k])) changed[k] = payload[k];
    }
    if (Object.keys(changed).length === 0) return; // nothing to do

    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    setAutoSaving(true);
    setSaveStatus(null);
    setSaveError('');
    try {
      let rowId = existingIdRef.current;
      if (!rowId) {
        const { data: ex, error: exErr } = await supabase
          .from('nurse_assessments').select('id')
          .eq('client_id', clientStrId).order('created_at', { ascending: false }).limit(1).maybeSingle();
        if (exErr) throw exErr;
        rowId = ex?.id || null;
      }

      if (rowId) {
        const { error } = await supabase.from('nurse_assessments')
          .update({ ...changed, client_id: clientStrId, client_uuid: clientDbId, completed_by: profile?.id || null, completed_by_name: profile?.full_name || null, updated_at: new Date().toISOString() })
          .eq('id', rowId);
        if (error) throw error;
        existingIdRef.current = rowId;
      } else {
        const { data: ins, error } = await supabase.from('nurse_assessments')
          .insert([payload]).select('id').single();
        if (error) throw error;
        existingIdRef.current = ins?.id || null;
      }

      // Success → advance the baseline and clear the device backup.
      baselineRef.current = pickContent(payload);
      try { localStorage.removeItem(lsKey(clientStrId)); } catch (_) {}
      setAutoSaveAt(new Date());
      setSaveStatus('saved');

      // Append-only recovery snapshot, throttled to ~once / 5 min so an
      // autosaved draft is always restorable even if the live row is later
      // wiped. Non-blocking; ignore failures.
      const nowMs = Date.now();
      if (existingIdRef.current && profile?.id && nowMs - lastVersionAtRef.current > 300000) {
        lastVersionAtRef.current = nowMs;
        createNurseAssessmentVersion(existingIdRef.current, payload, profile.id).catch(() => {});
      }
    } catch (err) {
      console.warn('Autosave failed:', err);
      setSaveStatus('error');
      setSaveError('Could not reach the server — your work is backed up on this device and will retry automatically. Do not close this tab until it saves.');
      // Keep the device backup; retry shortly.
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => { doAutoSave(); }, 15000);
    } finally {
      setAutoSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientStrId, clientDbId, profile, buildPayload, mirrorToDevice]);

  // Mirror to this device quickly (5s) on every change — the always-on safety net.
  useEffect(() => {
    if (!loadedRef.current || !loadOkRef.current) return;
    if (localMirrorTimer.current) clearTimeout(localMirrorTimer.current);
    localMirrorTimer.current = setTimeout(mirrorToDevice, 5000);
    return () => clearTimeout(localMirrorTimer.current);
  }, [formSnapshot, mirrorToDevice]);

  // Debounced server autosave — fires 30s after the last change.
  useEffect(() => {
    if (!loadedRef.current || !loadOkRef.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(doAutoSave, 30000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [formSnapshot, doAutoSave]);

  // Warn before leaving if a save is mid-flight or failed (unsynced work).
  useEffect(() => {
    const handler = (e) => {
      if (saveStatus === 'error' || autoSaving) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus, autoSaving]);

  // ─── SAVE HANDLER ─────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError('');
    try {
      // Auto-save current form state to DB first so AI gets the freshest data
      await doAutoSave();

      // Fetch the freshly-saved assessment record
      const { data: assessment } = await supabase
        .from('nurse_assessments')
        .select('*')
        .eq('client_id', clientStrId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Fall back to in-memory state if no DB record yet
      const payload = assessment || {
        primary_diagnosis: primaryDx,
        secondary_diagnoses: secondaryDx,
        hospitalizations,
        dependency_level: depLevel,
        adl_support_needed: adlSupport,
        equipment,
        other_equipment: otherEquip,
        feeding_method: feedingMethod,
        feeding_tolerance: feedingTol,
        respiratory_pattern: respPattern,
        airway_needs: airwayNeeds,
        neuro_diagnoses: neuroDx,
        behavioral_flags: behavFlags,
        skin_integrity: skinIntegrity,
        gu_status: guStatus,
        seizures,
        swallowing_issues: swallowing,
        contractures,
        caregiver_ability: cgAbility,
        caregiver_availability: cgAvail,
        skilled_interventions: skilledInterv,
        skilled_hours_requested: skilledHrs,
        unskilled_hours_requested: unskilledHrs,
        supervision_needs: supervNeeds,
        adl_unskilled: adlSupport2,
        safety_risks: safetyRisks,
        // Include nurse-typed notes so AI can expand them
        equipment_notes: equipNotes,
        feeding_notes: feedingNotes,
        functional_notes: funcNotes,
        respiratory_notes: respNotes,
        neuro_notes: neuroNotes,
        skin_notes: skinNotes,
        behavioral_notes: behavNotes,
        caregiver_notes: cgNotes,
        hours_justification: hrsJustification,
        preventive_strategies: preventive,
      };

      // seizures / swallowing_issues / contractures live on `clients`, not on
      // nurse_assessments. When the DB row was used above they came through as
      // undefined, so the prompt told the model "Seizures: No" for children who
      // do have a seizure disorder. Always overlay them from form state.
      const aiPayload = {
        ...payload,
        seizures,
        swallowing_issues: swallowing,
        contractures,
      };

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-narrative`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(aiPayload),
        }
      );

      const result = await res.json();
      if (!result.success) throw new Error(result.error);

      const { narratives } = result;
      // Section notes (1.2 – 1.9) — set AI display states AND sync to form fields
      if (narratives.equipment_notes)    { setAiEquipNotes(narratives.equipment_notes);    setEquipNotes(narratives.equipment_notes); }
      if (narratives.feeding_notes)      { setAiFeedingNotes(narratives.feeding_notes);    setFeedingNotes(narratives.feeding_notes); }
      if (narratives.functional_notes)   { setAiFuncNotes(narratives.functional_notes);    setFuncNotes(narratives.functional_notes); }
      if (narratives.respiratory_notes)  { setAiRespNotes(narratives.respiratory_notes);   setRespNotes(narratives.respiratory_notes); }
      if (narratives.neuro_notes)        { setAiNeuroNotes(narratives.neuro_notes);        setNeuroNotes(narratives.neuro_notes); }
      if (narratives.skin_notes)         { setAiSkinNotes(narratives.skin_notes);          setSkinNotes(narratives.skin_notes); }
      if (narratives.behavioral_notes)   { setAiBehavNotes(narratives.behavioral_notes);   setBehavNotes(narratives.behavioral_notes); }
      if (narratives.caregiver_notes)    { setAiCgNotes(narratives.caregiver_notes);       setCgNotes(narratives.caregiver_notes); }
      // Section 2 content
      if (narratives.hours_justification)    { setAiHrsJust(narratives.hours_justification);       setHrsJust(narratives.hours_justification); }
      if (narratives.preventive_strategies)  { setAiPreventive(narratives.preventive_strategies);  setPreventive(narratives.preventive_strategies); }
      if (narratives.emergency_protocol)     { setAiEmergProtocol(narratives.emergency_protocol);  setEmergProtocol(narratives.emergency_protocol); }
      if (narratives.medical_necessity_narrative) { setAiMedNarrative(narratives.medical_necessity_narrative); setMedNarrative(narratives.medical_necessity_narrative); }
      // Tables
      if (narratives.skilled_goals_rows?.length)    { setAiSkilledGoals(narratives.skilled_goals_rows);     setSkilledTable(narratives.skilled_goals_rows); }
      if (narratives.unskilled_goals_rows?.length)  { setAiUnskilledGoals(narratives.unskilled_goals_rows); setUnskilledTable(narratives.unskilled_goals_rows); }
      if (narratives.competency_audit_rows?.length) { setAiCompetencyAudit(narratives.competency_audit_rows); setCompetencyTable(narratives.competency_audit_rows); }
      if (narratives.teaching_plan_rows?.length)    { setAiTeachingPlan(narratives.teaching_plan_rows);     setTeachingTable(narratives.teaching_plan_rows); }
      // Guarded like every other field above — an unconditional `|| ''` here
      // meant a response that omitted either key actively wiped the existing
      // text instead of leaving it alone.
      if (narratives.skilled_hours_justification)   setAiSkilledJust(narratives.skilled_hours_justification);
      if (narratives.unskilled_hours_justification) setAiUnskilledJust(narratives.unskilled_hours_justification);
      setNarrativeGenerated(true);

      // Record that AI content was produced for this record.
      if (existingIdRef.current) {
        await supabase
          .from('nurse_assessments')
          .update({
            ai_narrative_generated: true,
            ai_narrative_generated_at: new Date().toISOString(),
          })
          .eq('id', existingIdRef.current);
      }
      setActiveSection(4);
    } catch (err) {
      setGenerateError(err.message || 'Generation failed — please try again');
    } finally {
      setGenerating(false);
    }
  };

  // `statusOverride` is how "Mark as Complete" saves: setFormStatus() only
  // lands on the next render, so reading formStatus here saved the first
  // "complete" click as a draft. A button's click event is ignored.
  const handleSave = async (statusOverride) => {
    const saveAs = typeof statusOverride === 'string' ? statusOverride : formStatus;
    // Don't allow an explicit save if the record never loaded cleanly — it
    // would write the current (possibly blank) form over saved data.
    if (!loadOkRef.current) {
      setSaveStatus('error');
      setSaveError('The saved assessment did not load. Please reload the page before saving to avoid overwriting existing data.');
      return;
    }
    setSaving(true);
    setSaveStatus(null);
    setSaveError('');
    try {
      // 1. Update clients table — verified contact + clinical tab fields
      const { error: clientError } = await supabase
        .from('clients')
        .update({
          // Patient basic info
          first_name:  patientInfo.firstName,
          last_name:   patientInfo.lastName,
          date_of_birth: patientInfo.dob || null,
          gender:      patientInfo.gender,
          language:    patientInfo.language,
          address:     patientInfo.address,
          city:        patientInfo.city,
          county:      patientInfo.county,
          state:       patientInfo.state,
          zip:         patientInfo.zip,
          // Guardian
          guardian_first_name:       guardian.firstName,
          guardian_last_name:        guardian.lastName,
          guardian_relationship:     guardian.relationship,
          guardian_phone:            guardian.phone,
          guardian_alt_phone:        guardian.altPhone,
          guardian_email:            guardian.email,
          guardian_lives_with_child: guardian.livesWithChild,
          // Emergency
          emergency_first_name:   emergency.firstName,
          emergency_last_name:    emergency.lastName,
          emergency_relationship: emergency.relationship,
          emergency_phone:        emergency.phone,
          emergency_alt_phone:    emergency.altPhone,
          emergency_email:        emergency.email,
          // PCP
          doctor_name:     pcp.name,
          doctor_practice: pcp.practice,
          doctor_address:  pcp.address,
          doctor_phone:    pcp.phone,
          doctor_fax:      pcp.fax,
          doctor_npi:      pcp.npi,
          // Ordering physician
          ordering_physician_name:      op.name,
          ordering_physician_specialty: op.specialty,
          ordering_physician_practice:  op.practice,
          ordering_physician_address:   op.address,
          ordering_physician_phone:     op.phone,
          ordering_physician_fax:       op.fax,
          ordering_physician_npi:       op.npi,
          // Clinical tab fields
          full_diagnoses: [primaryDx, secondaryDx].filter(Boolean).join(' | '),
          weight_lbs:   weight ? parseFloat(weight) : null,
          diet_description: diet,
          allergies:    allergies || null,
          current_medications: medications,
          hospitalizations: hospitalizations,
          // Medical flags
          seizures:          seizures,
          g_tube:            gTube,
          uses_oxygen:       usesOxygen,
          potty_trained:     pottyTrained,
          diapers:           wearsDiapers,
          contractures:      contractures,
          swallowing_issues: swallowing,
          has_allergies:     hasAllergies,
          medical_equipment: [...equipment, ...(otherEquip ? [`Other: ${otherEquip}`] : [])],
          // IEP — keep the older has_iep boolean in sync with the form's
          // iep_provided value so the two can't disagree. Only written when the
          // nurse actually answered, so a blank never clobbers an intake value.
          iep_provided: iepProvided || null,
          ...(iepProvided ? { has_iep: iepProvided === 'Yes' } : {}),
          // The client counts as assessed only once the assessment is complete;
          // saving a draft leaves the flag as it was.
          ...(saveAs === 'complete' ? {
            nurse_assessment_complete: true,
            nurse_assessment_date:     naDate || new Date().toISOString().split('T')[0],
          } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (clientError) throw Object.assign(clientError, { source: 'Patient profile update' });

      // 2. Insert or update nurse_assessments (no unique constraint — check first)
      const { data: existing } = await supabase
        .from('nurse_assessments')
        .select('id')
        .eq('client_id', clientStrId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const assessmentPayload = {
          client_id:              clientStrId,
          client_uuid:            clientDbId,
          assessment_date:        naDate || new Date().toISOString().split('T')[0],
          completed_by:           profile?.id || null,
          completed_by_name:      profile?.full_name || null,
          status:                 saveAs,
          // Section 1
          primary_diagnosis:      primaryDx,
          secondary_diagnoses:    secondaryDx,
          hospitalizations:       hospitalizations,
          equipment:              [...equipment, ...(otherEquip ? [`Other: ${otherEquip}`] : [])],
          other_equipment:        otherEquip,
          equipment_notes:        equipNotes,
          feeding_method:         feedingMethod,
          feeding_tolerance:      feedingTol,
          feeding_notes:          feedingNotes,
          dependency_level:       depLevel,
          adl_support_needed:     adlSupport,
          functional_notes:       funcNotes,
          respiratory_pattern:    respPattern,
          airway_needs:           airwayNeeds,
          respiratory_notes:      respNotes,
          neuro_diagnoses:        neuroDx,
          neuro_notes:            neuroNotes,
          skin_integrity:         skinIntegrity,
          gu_status:              guStatus,
          skin_notes:             skinNotes,
          behavioral_flags:       behavFlags,
          behavioral_notes:       behavNotes,
          caregiver_ability:      cgAbility,
          caregiver_availability: cgAvail,
          caregiver_notes:        cgNotes,
          med_necessity_narrative: medNarrative,
          skilled_needs_summary:  skilledNeeds,
          unskilled_needs_summary: unskilledNeeds,
          skilled_hours_justification:   aiSkilledJust,
          unskilled_hours_justification: aiUnskilledJust,
          // Section 2
          skilled_interventions:    skilledInterv,
          skilled_goals:            skilledTable,
          adl_unskilled:            adlSupport2,
          supervision_needs:        supervNeeds,
          unskilled_goals:          unskilledTable,
          skilled_hours_requested:  skilledHrs ? Number(skilledHrs) : null,
          unskilled_hours_requested: unskilledHrs ? Number(unskilledHrs) : null,
          hours_justification:      hrsJustification,
          safety_risks:             safetyRisks,
          preventive_strategies:    preventive,
          emergency_protocol:       emergProtocol,
          // Section 3
          competency_audit: competencyTable,
          teaching_plan:    teachingTable,
          iep_provided:     iepProvided || null,
          updated_at:       new Date().toISOString(),
        };

      let naError;
      let assessmentId;
      if (existing?.id) {
        // Record exists — update it
        const { error: updateErr } = await supabase
          .from('nurse_assessments')
          .update(assessmentPayload)
          .eq('id', existing.id);
        naError = updateErr;
        assessmentId = existing.id;
      } else {
        // No record yet — insert fresh
        const { data: insertedData, error: insertErr } = await supabase
          .from('nurse_assessments')
          .insert([assessmentPayload])
          .select('id');
        naError = insertErr;
        assessmentId = insertedData?.[0]?.id;
      }

      if (naError) throw Object.assign(naError, { source: 'Assessment data save' });

      // Sync autosave state to what we just persisted, and clear the device
      // backup so a stale copy can't prompt a needless restore later.
      existingIdRef.current = assessmentId || existingIdRef.current;
      baselineRef.current = pickContent(assessmentPayload);
      try { localStorage.removeItem(lsKey(clientStrId)); } catch (_) {}

      // 2.5 Create version record for audit trail
      if (assessmentId && profile?.id) {
        try {
          await createNurseAssessmentVersion(assessmentId, assessmentPayload, profile.id);
        } catch (versionErr) {
          console.warn('Version creation failed:', versionErr);
          // Don't throw — versioning is secondary to main save
        }
      }

      // 3. Log to activity log
      await supabase.from('activity_log').insert([{
        client_id:         clientDbId,
        client_ref:        clientStrId,
        activity_type:     'Nurse Assessment Completed',
        notes:             `Assessment completed by ${profile?.full_name || 'nurse'}`,
        performed_by_id:   profile?.id || null,
        performed_by_name: profile?.full_name || null,
      }]);

      setSaveStatus('saved');
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
      const src = err?.source ? `[${err.source}] ` : '';
      setSaveError(src + (err?.message || err?.details || JSON.stringify(err) || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // ─── DOCUMENT GENERATION ─────────────────────────────────────
  const [docGenerating, setDocGenerating] = useState(false);
  const [docError, setDocError]           = useState('');
  const [docDriveUrl, setDocDriveUrl]     = useState('');
  const [docVersion, setDocVersion]       = useState(null);  // version number generated
  const [docVersionDate, setDocVersionDate] = useState('');  // when last generated

  const handleGenerateDoc = async () => {
    setDocGenerating(true);
    setDocError('');
    try {
      // 0. Flush pending edits first. Both fetches below read from the DB, so
      //    anything typed inside the 30s autosave debounce would otherwise be
      //    silently missing from the generated document.
      await doAutoSave();

      // 1. Fetch full client record (PHI merged locally — never sent to AI)
      const { data: clientRow, error: clientErr } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();
      if (clientErr || !clientRow) throw new Error('Could not load patient record');

      // The clients table only updates on an explicit Save, but the document
      // prints several client-level fields (allergies, medications, diet,
      // weight, IEP). Overlay what is on screen so the .docx always matches the
      // form the nurse is looking at.
      const client = {
        ...clientRow,
        weight_lbs:          weight ? parseFloat(weight) : clientRow.weight_lbs,
        allergies:           allergies || clientRow.allergies,
        has_allergies:       hasAllergies,
        current_medications: medications || clientRow.current_medications,
        diet_description:    diet || clientRow.diet_description,
        iep_provided:        iepProvided || clientRow.iep_provided,
        seizures,
      };

      // 2. Fetch latest assessment record
      const { data: assessment } = await supabase
        .from('nurse_assessments')
        .select('*')
        .eq('client_id', clientStrId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Whether the document may say a physician's order is on file.
      const [cycles, cycleFacts] = await Promise.all([listCycles(id), loadCycleFacts(id)]);
      const physicianOrder = physicianOrderOnFile(
        cycleFacts,
        cycles.find(c => c.id === assessment?.cycle_id) || null,
        naDate || new Date().toISOString().split('T')[0],
      );

      const narratives = {
        // Section notes (1.2 – 1.9)
        equipment_notes: aiEquipNotes,
        feeding_notes: aiFeedingNotes,
        functional_notes: aiFuncNotes,
        respiratory_notes: aiRespNotes,
        neuro_notes: aiNeuroNotes,
        skin_notes: aiSkinNotes,
        behavioral_notes: aiBehavNotes,
        caregiver_notes: aiCgNotes,
        // Section 2 content
        hours_justification: aiHrsJust,
        preventive_strategies: aiPreventive,
        // Tables
        skilled_goals_rows: aiSkilledGoals,
        unskilled_goals_rows: aiUnskilledGoals,
        competency_audit_rows: aiCompetencyAudit,
        teaching_plan_rows: aiTeachingPlan,
        // Narratives
        medical_necessity_narrative: aiMedNarrative,
        skilled_hours_justification: aiSkilledJust,
        unskilled_hours_justification: aiUnskilledJust,
        emergency_protocol: aiEmergProtocol,
      };

      const params = {
        client,
        assessment: assessment || {
          primary_diagnosis: primaryDx,
          secondary_diagnoses: secondaryDx,
          equipment,
          other_equipment: otherEquip,
          equipment_notes: equipNotes,
          feeding_method: feedingMethod,
          feeding_tolerance: feedingTol,
          feeding_notes: feedingNotes,
          dependency_level: depLevel,
          adl_support_needed: adlSupport,
          functional_notes: funcNotes,
          respiratory_pattern: respPattern,
          airway_needs: airwayNeeds,
          respiratory_notes: respNotes,
          neuro_diagnoses: neuroDx,
          neuro_notes: neuroNotes,
          skin_integrity: skinIntegrity,
          gu_status: guStatus,
          skin_notes: skinNotes,
          behavioral_flags: behavFlags,
          behavioral_notes: behavNotes,
          caregiver_ability: cgAbility,
          caregiver_availability: cgAvail,
          caregiver_notes: cgNotes,
          med_necessity_narrative: medNarrative,
          skilled_interventions: skilledInterv,
          skilled_goals: skilledTable,
          adl_unskilled: adlSupport2,
          supervision_needs: supervNeeds,
          unskilled_goals: unskilledTable,
          skilled_hours_requested: skilledHrs,
          unskilled_hours_requested: unskilledHrs,
          hours_justification: hrsJustification,
          safety_risks: safetyRisks,
          preventive_strategies: preventive,
          emergency_protocol: emergProtocol,
          competency_audit: competencyTable,
          teaching_plan: teachingTable,
        },
        narratives,
        completedByName: profile?.full_name || '',
        assessmentDate: naDate || new Date().toISOString().split('T')[0],
        physicianOrder,
      };

      // 3. Generate .docx blob (browser download)
      const blob = await downloadAssessmentDoc(params);

      // 4. Upload to Google Drive (if secret is configured)
      const rootFolderId = CLIENT_DRIVE_ROOT;
      if (rootFolderId) {
        const filename = `AHFC_GAPP_${clientStrId}_${params.assessmentDate}.docx`;
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < uint8Array.length; i += 8192) {
          binary += String.fromCharCode(...uint8Array.subarray(i, i + 8192));
        }
        const base64 = btoa(binary);

        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-to-drive`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ fileBase64: base64, filename, clientId: clientStrId, rootFolderId }),
          }
        );

        const driveResult = await res.json();
        if (driveResult.success && driveResult.webViewLink) {
          setDocDriveUrl(driveResult.webViewLink);
          setDocVersion(assessment?.version_number || null);
          setDocVersionDate(new Date().toISOString());

          if (assessment?.id) {
            await supabase
              .from('nurse_assessments')
              .update({ drive_document_url: driveResult.webViewLink })
              .eq('id', assessment.id);
          }
          await addDocument({
            client_id: id,
            document_name: filename,
            document_type: 'Nurse Assessment',
            drive_url: driveResult.webViewLink,
            notes: `Auto-generated GAPP assessment — ${params.assessmentDate}`,
            uploaded_by_id: profile?.id || null,
            uploaded_by_name: profile?.full_name || null,
          });
        } else if (!driveResult.success) {
          throw new Error(`Drive upload failed: ${driveResult.error}`);
        }
      }
    } catch (err) {
      console.error('Document generation error:', err);
      setDocError(err.message || 'Document generation failed — please try again');
    } finally {
      setDocGenerating(false);
    }
  };

  // ─── LOADING STATE ────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 14 }}>
      <div className="loading-spinner" style={{ width: 36, height: 36 }} />
      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", color: NAVY, fontSize: 14 }}>Loading patient record…</div>
    </div>
  );

  // ─── LOAD-FAILURE GUARD ───────────────────────────────────────
  // If the saved record could not be read, do NOT render the editable form —
  // editing on top of a blank load is exactly what wiped data before.
  if (loadError) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>⚠️</div>
      <div style={{ fontFamily: "'Trebuchet MS', sans-serif", color: 'var(--danger)', fontSize: 15, fontWeight: 700, maxWidth: 460 }}>
        {loadError}
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '10px 22px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
      >
        Reload page
      </button>
    </div>
  );

  const sections = ['Patient Info', '1: Assessment', '2: Care Plan', '3: Caregiver', 'Review'];

  // Tab completion checks
  const tabComplete = [
    patientInfo.firstName && patientInfo.lastName && patientInfo.dob && guardian.firstName && guardian.lastName,
    primaryDx && feedingMethod.length > 0 && depLevel && respPattern,
    skilledHrs && unskilledHrs && emergProtocol,
    competencyTable.some(row => row[0]),
    false, // Review tab — not tracked
  ];
  const tabHasData = [
    patientInfo.firstName || patientInfo.lastName || guardian.firstName || guardian.lastName,
    primaryDx || feedingMethod.length > 0 || depLevel,
    skilledHrs || unskilledHrs || emergProtocol,
    competencyTable.some(row => row[0]),
    false,
  ];

  return (
    <div style={{ background: 'var(--accent-bg)', minHeight: '100vh', fontFamily: "'Trebuchet MS', sans-serif" }}>
      <div style={{ padding: '0 24px', background: 'var(--bg)' }}>
        <ClientTrail clientId={id} sticky={false} crumbs={[...tabCrumbs(id, 'clinical', 'assessments'), { label: 'Nurse assessment' }]} />
      </div>

      {/* Header */}
      <div style={{ background: NAVY, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <button onClick={() => navigate(clientTabPath(id, 'clinical', 'assessments'))}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontFamily: "'Trebuchet MS', sans-serif" }}>
          ← Back to Profile
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            {patientInfo.firstName ? `${patientInfo.firstName} ${patientInfo.lastName}` : 'Loading…'}
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: formStatus === 'complete' ? GREEN : AMBER, color: '#fff' }}>
              {formStatus === 'complete' ? '✓ Complete' : 'Draft'}
            </span>
          </div>
          <div style={{ color: 'var(--accent-border)', fontSize: 11 }}>
            🔒 PHI Protection Active &nbsp;·&nbsp; GAPP Unified Assessment v3.0 &nbsp;·&nbsp; {clientStrId || id}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {autoSaving && <span style={{ color: 'var(--accent-border)', fontSize: 11 }}>Autosaving…</span>}
          {!autoSaving && autoSaveAt && (
            <span style={{ color: 'var(--accent-border)', fontSize: 11 }}>
              Autosaved {autoSaveAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {saveStatus === 'saved' && <span style={{ color: 'var(--on-navy-success)', fontSize: 12, fontWeight: 700 }}>✓ Saved</span>}
          {saveStatus === 'error' && (
            <span style={{ color: 'var(--on-navy-danger)', fontSize: 12, fontWeight: 700, maxWidth: 300, textAlign:'right' }}>
              ⚠ Save failed{saveError ? `: ${saveError}` : ''}
            </span>
          )}
          <button onClick={handleSave} disabled={saving} style={{
            background: saving ? 'var(--border2)' : GREEN, border: 'none', borderRadius: 7,
            color: '#fff', padding: '8px 18px', cursor: saving ? 'default' : 'pointer',
            fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, fontWeight: 700
          }}>
            {saving ? 'Saving…' : '💾 Save & Update Profile'}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 14px' }}>

        {/* Nav */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {sections.map((s, i) => {
            const dotColor = i < sections.length - 1
              ? (tabComplete[i] ? 'var(--success)' : tabHasData[i] ? AMBER : 'var(--border2)')
              : 'transparent';
            return (
              <button key={i} onClick={() => setActiveSection(i)} style={{
                padding: '8px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                background: activeSection === i ? NAVY : LIGHT,
                color: activeSection === i ? '#fff' : NAVY,
                fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {i < sections.length - 1 && (
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                )}
                {s}
              </button>
            );
          })}
        </div>
        <ProgBar step={activeSection + 1} total={sections.length} />

        {/* ═══════════════════════════════════════════════════════
            TAB 0 — PATIENT INFO & CONTACT VERIFICATION
        ═══════════════════════════════════════════════════════ */}
        {activeSection === 0 && (
          <div style={card}>
            <SHdr n="👤" title="Patient, Contact & Physician Verification" color={NAVY} />
            <div style={pad}>
              {notice(AMBER, 'var(--warn-bg)', '📋',
                '<strong>Nurse — please verify all information below</strong> against documents and people present at the visit. Correct anything wrong or missing. All updates save back to the patient profile.')}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 8 }}>
                <F label="Assessment Date (NA Date)" half>
                  <input type="date" style={inp} value={naDate} onChange={e => setNaDate(e.target.value)} />
                </F>
                <F label="IEP Provided?" half>
                  <Radio options={['Yes', 'No', 'N/A']} value={iepProvided} onChange={setIepProvided} />
                </F>
              </div>

              <VerifyCard title="Patient Information" icon="🧒" color={NAVY}
                note="Verify child's details against ID or Medicaid card"
                data={patientInfo} onChange={updPatient}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'dob', label: 'Date of Birth', type: 'date', third: true },
                  { key: 'age', label: 'Age (auto)', third: true, readOnly: true },
                  { key: 'gender', label: 'Gender', third: true },
                  { key: 'language', label: 'Primary Language', half: true },
                  { key: 'medicaidId', label: 'Medicaid ID (verify only)', half: true, readOnly: true },
                  { key: 'address', label: 'Home Address' },
                  { key: 'city', label: 'City', third: true },
                  { key: 'county', label: 'County', third: true },
                  { key: 'zip', label: 'ZIP Code', third: true },
                ]}
              />

              <VerifyCard title="Guardian / Parent" icon="👨‍👩‍👧" color={STEEL}
                note="Verify with the parent/guardian present at the visit"
                data={guardian} onChange={updGuardian}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'relationship', label: 'Relationship to Child', half: true },
                  { key: 'phone', label: 'Primary Phone', third: true },
                  { key: 'altPhone', label: 'Alternate Phone', third: true },
                  { key: 'email', label: 'Email Address', third: true },
                  { key: 'livesWithChild', label: 'Lives with Child?', half: true },
                ]}
              />

              <VerifyCard title="Emergency Contact" icon="🚨" color={RED}
                note="Confirm who to call in an emergency — must be different from guardian"
                data={emergency} onChange={updEmergency}
                fields={[
                  { key: 'firstName', label: 'First Name', half: true },
                  { key: 'lastName', label: 'Last Name', half: true },
                  { key: 'relationship', label: 'Relationship to Child', half: true },
                  { key: 'phone', label: 'Primary Phone', third: true },
                  { key: 'altPhone', label: 'Alternate Phone', third: true },
                  { key: 'email', label: 'Email Address', third: true },
                ]}
              />

              <VerifyCard title="Primary Care Physician (PCP)" icon="🩺" color={TEAL}
                note="Verify against physician order or Medicaid paperwork"
                data={pcp} onChange={updPcp}
                fields={[
                  { key: 'name', label: 'Physician Name', half: true },
                  { key: 'practice', label: 'Practice / Hospital', half: true },
                  { key: 'address', label: 'Address' },
                  { key: 'phone', label: 'Phone', third: true },
                  { key: 'fax', label: 'Fax', third: true },
                  { key: 'npi', label: 'NPI Number', third: true },
                ]}
              />

              <VerifyCard title="Ordering Physician / Specialist" icon="👨‍⚕️" color={PURPLE}
                note="Verify against the physician order — required for GAPP authorization"
                data={op} onChange={updOp}
                fields={[
                  { key: 'name', label: 'Physician Name', half: true },
                  { key: 'specialty', label: 'Specialty', half: true },
                  { key: 'practice', label: 'Practice / Hospital', half: true },
                  { key: 'address', label: 'Address', half: true },
                  { key: 'phone', label: 'Phone', third: true },
                  { key: 'fax', label: 'Fax', third: true },
                  { key: 'npi', label: 'NPI Number', third: true },
                ]}
              />

              {notice(GREEN, 'var(--success-bg)', '✓',
                'All corrections above will update the patient profile for all staff and appear on the generated GAPP document.')}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════
            TAB 1 — SECTION 1: NURSING ASSESSMENT
        ═══════════════════════════════════════════════════════ */}
        {activeSection === 1 && (
          <div style={card}>
            <SHdr n="1" title="Section 1 — Nursing Assessment" color={NAVY} />

            {narrativeGenerated && (
              <div style={{ padding: '10px 22px 0' }}>
                {notice(GREEN, 'var(--success-bg)', '✨', 'AI-generated content has been applied to this section. Review and edit as needed.')}
              </div>
            )}

            <SubH title="1.1  Diagnoses & Vitals  →  updates Clinical Tab" />
            <div style={pad}>
              {notice(AMBER, 'var(--warn-bg)', '✎', 'These fields auto-update <strong>Diagnosis & Vitals</strong> in the patient\'s Clinical tab on save.')}
              <F label="Primary Diagnosis (ICD-10, status & devices)"><TA value={primaryDx} onChange={setPrimaryDx} rows={3} placeholder="e.g. Autism Spectrum Disorder F84.0, nonverbal; Epilepsy G40.909" /></F>
              <F label="Secondary Diagnoses"><TA value={secondaryDx} onChange={setSecondaryDx} rows={2} /></F>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <F label="Weight (lbs)" half><TI type="number" value={weight} onChange={setWeight} placeholder="e.g. 64.4" /></F>
                <F label="Diet / Dietary Restrictions" half><TI value={diet} onChange={setDiet} placeholder="e.g. No sugar, pureed" /></F>
                <F label="Allergies" half><TI value={allergies} onChange={setAllergies} placeholder="None / list allergies" /></F>
                <F label="Current Medications" half><TI value={medications} onChange={setMedications} placeholder="e.g. Divalproex 500mg, Lamotrigine 150mg" /></F>
              </div>
              <F label="Most Recent Hospitalizations"><TA value={hospitalizations} onChange={setHosp} rows={2} /></F>
            </div>

            <SubH title="Medical Flags  →  updates Clinical Tab indicators" />
            <div style={pad}>
              {notice(AMBER, 'var(--warn-bg)', '✎', 'These toggles update the coloured dot indicators in the Clinical tab on save.')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
                <Toggle label="Seizures" value={seizures} onChange={setSeizures} tc={RED} />
                <Toggle label="G-Tube" value={gTube} onChange={setGTube} />
                <Toggle label="Uses Oxygen" value={usesOxygen} onChange={setUsesOxygen} />
                <Toggle label="Potty Trained" value={pottyTrained} onChange={setPottyTrained} />
                <Toggle label="Wears Diapers" value={wearsDiapers} onChange={setWearsDiapers} />
                <Toggle label="Contractures" value={contractures} onChange={setContractures} tc={RED} />
                <Toggle label="Swallowing Issues" value={swallowing} onChange={setSwallowing} tc={RED} />
                <Toggle label="Has Allergies" value={hasAllergies} onChange={setHasAllergies} />
              </div>
            </div>

            <SubH title="1.2  Medical Equipment & Technology  →  updates Equipment tab" />
            <div style={pad}>
              <Checks options={['Oxygen','Suction','Nebulizer','CPAP / BiPAP','Pulse Oximeter','G-Tube / J-Tube','Feeding Pump','Tracheostomy','Ventilator']} value={equipment} onChange={setEquipment} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 6 }}>
                <label style={{ ...lbl, marginBottom: 0, whiteSpace: 'nowrap' }}>Other (specify):</label>
                <input type="text" style={{ ...inp, maxWidth: 320 }} value={otherEquip} onChange={e => setOtherEquip(e.target.value)} placeholder="e.g. Pulse oximeter monitor" />
              </div>
              <F label="RN Clinical Notes – Equipment"><TA value={equipNotes} onChange={setEquipNotes} /></F>
            </div>

            <SubH title="1.3  Feeding Assessment" />
            <div style={pad}>
              <label style={lbl}>Feeding Method</label>
              <Checks options={['Oral','G-Tube','Pump Feeding','J-Tube','Other']} value={feedingMethod} onChange={setFeedingMethod} />
              <label style={{ ...lbl, marginTop: 10 }}>Tolerance</label>
              <Radio options={['Good','Fair','Poor']} value={feedingTol} onChange={setFeedingTol} />
              <F label="RN Feeding Assessment Notes"><TA value={feedingNotes} onChange={setFeedingNotes} /></F>
            </div>

            <SubH title="1.4  Mobility & ADL Status" />
            <div style={pad}>
              <label style={lbl}>Dependency Level</label>
              <Radio options={['Independent','Requires Assistance','Dependent','Dependent – All ADLs']} value={depLevel} onChange={setDepLevel} />
              <label style={{ ...lbl, marginTop: 10 }}>ADLs Requiring Support</label>
              <Checks options={['Feeding','Bathing','Grooming','Toileting','Dressing','Transfers','Repositioning','Mobility']} value={adlSupport} onChange={setAdlSupport} />
              <F label="RN Functional Assessment Notes"><TA value={funcNotes} onChange={setFuncNotes} /></F>
            </div>

            <SubH title="1.5  Respiratory Assessment" />
            <div style={pad}>
              <label style={lbl}>Respiratory Pattern</label>
              <Radio options={['Normal','Labored','Retractions','Tachypnea']} value={respPattern} onChange={setRespPattern} />
              <label style={{ ...lbl, marginTop: 10 }}>Airway Needs</label>
              <Checks options={['Trach Care','Suctioning','Oxygen','Nebulizer']} value={airwayNeeds} onChange={setAirwayNeeds} />
              <F label="RN Respiratory Assessment Notes"><TA value={respNotes} onChange={setRespNotes} /></F>
            </div>

            <SubH title="1.6  Neurological Assessment" />
            <div style={pad}>
              <Checks options={['Cerebral Palsy','Seizure Disorder / Epilepsy','Traumatic Brain Injury','Neuromuscular Disorder','Global Dev. Delay w/ Neuro Impairment','Hydrocephalus (VP Shunt / ETV)']} value={neuroDx} onChange={setNeuroDx} />
              <F label="RN Neurological Notes (include last seizure date if applicable)"><TA value={neuroNotes} onChange={setNeuroNotes} /></F>
            </div>

            <SubH title="1.7  Skin & GU Assessment" />
            <div style={pad}>
              <label style={lbl}>Skin Integrity</label>
              <Radio options={['Intact','Rash','Breakdown','Wound / Granulation Tissue','Other']} value={skinIntegrity} onChange={setSkinIntegrity} />
              <label style={{ ...lbl, marginTop: 10 }}>GU Status</label>
              <Radio options={['Normal','Incontinent','Other']} value={guStatus} onChange={setGuStatus} />
              <F label="RN Skin / GU Assessment Notes"><TA value={skinNotes} onChange={setSkinNotes} /></F>
            </div>

            <SubH title="1.8  Behavioral & Safety Considerations" />
            <div style={pad}>
              <Checks options={['Requires Redirection','Wandering Risk','Aggression','Self-Injury Risk','Insufficient Supervision Risk']} value={behavFlags} onChange={setBehavFlags} />
              <F label="RN Behavioral & Safety Notes"><TA value={behavNotes} onChange={setBehavNotes} /></F>
            </div>

            <SubH title="1.9  Caregiver Status" />
            <div style={pad}>
              <label style={lbl}>Caregiver Ability</label>
              <Radio options={['Independent','Needs Training','Limited Ability','Unable']} value={cgAbility} onChange={setCgAbility} />
              <label style={{ ...lbl, marginTop: 10 }}>Caregiver Availability</label>
              <Radio options={['Full-Time','Part-Time','Limited','Not Available']} value={cgAvail} onChange={setCgAvail} />
              <F label="Caregiver Notes"><TA value={cgNotes} onChange={setCgNotes} /></F>
            </div>

            <SubH title="1.10  Medical Necessity Summary" />
            <div style={pad}>
              {notice(GREEN, 'var(--success-bg)', '💡', '<strong>AI Assist available.</strong> Leave the narrative blank — Claude will draft this from your clinical inputs when you click "Generate GAPP Document".')}
              <F label="Medical Necessity Narrative (or leave blank for AI generation)"><TA value={medNarrative} onChange={setMedNarrative} rows={5} placeholder="Clinical narrative supporting medical necessity…" /></F>
              <F label="Identified Skilled Nursing Needs"><TA value={skilledNeeds} onChange={setSkilledNeeds} rows={3} /></F>
              <F label="Identified Unskilled (CNA / PSS) Needs"><TA value={unskilledNeeds} onChange={setUnskilledNeeds} rows={3} /></F>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════
            TAB 2 — SECTION 2: CARE PLAN
        ═══════════════════════════════════════════════════════ */}
        {activeSection === 2 && (
          <div style={card}>
            <SHdr n="2" title="Section 2 — Nursing Care Plan" color={STEEL} />

            {narrativeGenerated && (
              <div style={{ padding: '10px 22px 0' }}>
                {notice(GREEN, 'var(--success-bg)', '✨', 'AI-generated content has been applied to this section. Review and edit as needed.')}
              </div>
            )}

            <SubH title="2.1  Skilled Nursing Care Plan" />
            <div style={pad}>
              <label style={lbl}>Skilled Nursing Interventions</label>
              <Checks options={['Medication Administration','Trach Care','Suctioning','Respiratory Monitoring','Seizure / Neuro Monitoring','Tube Feeding / Pump Mgmt','Wound Care','Vital Signs Monitoring','Infection Surveillance','Caregiver Education','IV / Line Mgmt','Other']} value={skilledInterv} onChange={setSkilledInterv} />
            </div>
            <TableSec columns={['Clinical Domain','Measurable Goal','Skilled Intervention & Actions','Frequency / Target']}
              data={skilledTable} onChange={updTable(setSkilledTable)} addRow={addRow(setSkilledTable)} />

            <SubH title="2.2  Unskilled Care Plan (CNA / PSS)" />
            <div style={pad}>
              <label style={lbl}>ADL / IADL Support Required</label>
              <Checks options={['Feeding','Bathing','Grooming','Toileting','Dressing','Transfers','Repositioning','Mobility / Walking','Meal Preparation','Laundry / Cleaning','School / Transport Assist.','Housekeeping','Errand Support','Supply Management','Other']} value={adlSupport2} onChange={setAdlSupport2} />
              <label style={{ ...lbl, marginTop: 12 }}>Supervision Required</label>
              <Checks options={['Safety Supervision','One-to-One Supervision','Overnight Safety Supervision','Frequent Redirection for Safety','Behavioral Support','Supervision – Unsafe Wandering']} value={supervNeeds} onChange={setSupervNeeds} />
            </div>
            <TableSec columns={['Clinical Domain','Measurable Goal','Unskilled Tasks & Actions','Frequency / Target']}
              data={unskilledTable} onChange={updTable(setUnskilledTable)} addRow={addRow(setUnskilledTable)} />

            <SubH title="2.3  Requested Hours & Justification" />
            <div style={pad}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>RN Skilled Hours / Week</label>
                  <input type="number" style={inp} value={skilledHrs} onChange={e => setSkilledHrs(e.target.value)} placeholder="e.g. 12" />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>PSS / CNA Hours / Week</label>
                  <input type="number" style={inp} value={unskilledHrs} onChange={e => setUnskilledHrs(e.target.value)} placeholder="e.g. 56" />
                </div>
                <div style={{ flex: '1 1 160px', background: LIGHT, borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontSize: 11, color: NAVY, fontWeight: 700 }}>Total Hours / Week</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: NAVY }}>
                    {Number(skilledHrs || 0) + Number(unskilledHrs || 0) || '—'}
                  </div>
                </div>
              </div>
              <F label="Hours Justification — Medical Necessity"><TA value={hrsJustification} onChange={setHrsJust} rows={5} /></F>
            </div>

            <SubH title="2.4  Safety Risk & Monitoring Plan" />
            <div style={pad}>
              <label style={lbl}>Risks Identified</label>
              <Checks options={['Airway Compromise','Aspiration','Seizure','Fall','Infection','Behavioral Escalation','Insufficient Supervision','G-Tube Complication','Skin Breakdown','Other']} value={safetyRisks} onChange={setSafetyRisks} />
              <F label="Preventive Strategies & Monitoring Plan"><TA value={preventive} onChange={setPreventive} rows={4} /></F>
              <F label="Emergency Response Protocol (seizure plan, airway management, escalation contacts)"><TA value={emergProtocol} onChange={setEmergProtocol} rows={4} /></F>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════
            TAB 3 — SECTION 3: CAREGIVER TRAINING
        ═══════════════════════════════════════════════════════ */}
        {activeSection === 3 && (
          <div style={card}>
            <SHdr n="3" title="Section 3 — Caregiver Skills Assessment & Training Plan" color={TEAL} />

            {narrativeGenerated && (
              <div style={{ padding: '10px 22px 0' }}>
                {notice(GREEN, 'var(--success-bg)', '✨', 'AI-generated content has been applied to this section. Review and edit as needed.')}
              </div>
            )}
            <div style={{ ...pad, paddingBottom: 6 }}>
              <p style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', margin: 0 }}>
                The Provider RN has assessed the primary caregiver's current ability to perform required care tasks.
              </p>
            </div>
            <SubH title="3.1  Competency Audit" />
            <TableSec columns={['Required Care Task','Clinical Risk / Justification','Caregiver Status (Able / Needs Training)','Status (Completed / Pending)']}
              data={competencyTable} onChange={updTable(setCompetencyTable)} addRow={addRow(setCompetencyTable)} />
            <SubH title="3.2  Caregiver Teaching Plan" />
            <TableSec columns={['Teaching Goal / Objective','Teaching Method & Content','Completion Date','Status (Completed / Pending)']}
              data={teachingTable} onChange={updTable(setTeachingTable)} addRow={addRow(setTeachingTable)} />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════
            TAB 4 — REVIEW & SUBMIT
        ═══════════════════════════════════════════════════════ */}
        {activeSection === 4 && (
          <div style={card}>
            <SHdr n="✓" title="Review & Submit" color={GREEN} />
            <div style={pad}>

              <div style={{ background: LIGHT, borderRadius: 8, padding: 18, marginBottom: 18 }}>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 12 }}>Assessment Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: 12 }}>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Patient:</span> {[patientInfo.firstName, patientInfo.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Guardian:</span> {[guardian.firstName, guardian.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Emergency Contact:</span> {[emergency.firstName, emergency.lastName].filter(Boolean).join(' ') || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Primary Dx:</span> {primaryDx || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>PCP:</span> {pcp.name || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Ordering MD:</span> {op.name || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Skilled Hrs/wk:</span> {skilledHrs || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>PSS Hrs/wk:</span> {unskilledHrs || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Seizures:</span> {seizures ? <span style={{ color: RED, fontWeight: 700 }}>Yes ⚠</span> : 'No'}</div>
                  <div><span style={{ color: NAVY, fontWeight: 700 }}>Dependency:</span> {depLevel || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
                </div>
              </div>

              {notice(AMBER, 'var(--warn-bg)', '📋', `
                <strong>On Save — the following updates happen automatically:</strong><br/>
                ✓ Patient basic info updated (name, address, language, gender)<br/>
                ✓ Guardian / Parent contact details updated<br/>
                ✓ Emergency Contact updated<br/>
                ✓ PCP &amp; Ordering Physician details updated<br/>
                ✓ Diagnosis &amp; Vitals updated (diagnoses, weight, diet, allergies, medications)<br/>
                ✓ Medical Flags updated (seizures, G-tube, oxygen, diapers, swallowing, contractures)<br/>
                ✓ Equipment list updated<br/>
                ✓ Assessment banner flips to ✓ Assessment Complete<br/>
                ✓ Activity log entry created
              `)}

              {notice(GREEN, 'var(--success-bg)', '📄', `
                <strong>Generated Document — saved to:</strong><br/>
                → Google Drive: /AHFC Patient Records/[${id}]/Assessments/<br/>
                → Documents tab in patient profile (link saved automatically)<br/>
                → PHI merged locally before upload — AI never receives patient identity
              `)}

              {formStatus !== 'complete' && (
                <div style={{ background: LIGHT, border: `1.5px solid ${GREEN}`, borderRadius: 8, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: NAVY }}>Mark Assessment as Complete</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Once marked complete, the status badge will show ✓ Complete. You can still edit and re-save.</div>
                  </div>
                  <button onClick={async () => { setFormStatus('complete'); await handleSave('complete'); }} disabled={saving} style={{
                    padding: '10px 20px', background: saving ? 'var(--border2)' : GREEN, border: 'none', borderRadius: 8,
                    color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif", whiteSpace: 'nowrap'
                  }}>
                    ✓ Mark as Complete
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                <button onClick={handleSave} disabled={saving} style={{
                  flex: '1 1 180px', padding: '12px 0', background: LIGHT,
                  border: `1.5px solid ${STEEL}`, borderRadius: 8, color: NAVY,
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: "'Trebuchet MS', sans-serif"
                }}>💾 Save & Update Patient Profile</button>
                <button onClick={handleGenerate} disabled={generating} style={{
                  flex: '1 1 240px', padding: '12px 0',
                  background: generating ? 'var(--border2)' : NAVY,
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, fontSize: 13, cursor: generating ? 'default' : 'pointer',
                  fontFamily: "'Trebuchet MS', sans-serif"
                }}>
                  {generating ? '⏳ Generating narrative…' : '⚡ Generate Nurse Assessment Report'}
                </button>
              </div>

              {generateError && (
                <div style={{ background: 'var(--danger-bg)', border: '1.5px solid var(--danger)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--danger)' }}>
                  ⚠ {generateError}
                </div>
              )}

              {narrativeGenerated && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ background: 'var(--success-bg)', border: `1px solid ${GREEN}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--success)' }}>
                    ✅ <strong>All 14 sections generated.</strong> Review and edit each section below before generating the final document. AI has drafted any empty nurse notes from the clinical data.
                  </div>

                  {/* ── Section 1 Notes ─────────────────────────────── */}
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, paddingBottom: 6, borderBottom: `2px solid ${NAVY}` }}>
                    Section 1 — Clinical Notes
                  </div>
                  {[
                    { label: '1.2  RN Equipment Notes', value: aiEquipNotes, onChange: setAiEquipNotes, rows: 3 },
                    { label: '1.3  RN Feeding Assessment Notes', value: aiFeedingNotes, onChange: setAiFeedingNotes, rows: 3 },
                    { label: '1.4  RN Functional Assessment Notes', value: aiFuncNotes, onChange: setAiFuncNotes, rows: 3 },
                    { label: '1.5  RN Respiratory Assessment Notes', value: aiRespNotes, onChange: setAiRespNotes, rows: 3 },
                    { label: '1.6  RN Neurological Notes', value: aiNeuroNotes, onChange: setAiNeuroNotes, rows: 3 },
                    { label: '1.7  RN Skin / GU Assessment Notes', value: aiSkinNotes, onChange: setAiSkinNotes, rows: 3 },
                    { label: '1.8  RN Behavioral & Safety Notes', value: aiBehavNotes, onChange: setAiBehavNotes, rows: 3 },
                    { label: '1.9  Caregiver Notes', value: aiCgNotes, onChange: setAiCgNotes, rows: 3 },
                  ].map(({ label, value, onChange, rows }) => (
                    <div key={label} style={{ marginBottom: 14 }}>
                      <label style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {label}
                        <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                      </label>
                      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
                        style={{ width: '100%', border: `1.5px solid ${GREEN}`, borderRadius: 6, padding: '10px 12px', fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: 'var(--text)', background: 'var(--bg2)', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
                      />
                    </div>
                  ))}

                  {/* ── Section 2 Content ────────────────────────────── */}
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: STEEL, marginBottom: 10, marginTop: 20, paddingBottom: 6, borderBottom: `2px solid ${STEEL}` }}>
                    Section 2 — Care Plan Justifications
                  </div>
                  {[
                    { label: '1.10  Medical Necessity Narrative', value: aiMedNarrative, onChange: setAiMedNarrative, rows: 5 },
                    { label: '2.3  Skilled Hours Justification', value: aiSkilledJust, onChange: setAiSkilledJust, rows: 3 },
                    { label: '2.3  Unskilled Hours Justification', value: aiUnskilledJust, onChange: setAiUnskilledJust, rows: 3 },
                    { label: '2.3  Hours Justification (Combined)', value: aiHrsJust, onChange: setAiHrsJust, rows: 4 },
                    { label: '2.4  Preventive Strategies & Monitoring Plan', value: aiPreventive, onChange: setAiPreventive, rows: 4 },
                    { label: '2.4  Emergency Response Protocol', value: aiEmergProtocol, onChange: setAiEmergProtocol, rows: 4 },
                  ].map(({ label, value, onChange, rows }) => (
                    <div key={label} style={{ marginBottom: 14 }}>
                      <label style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {label}
                        <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                      </label>
                      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
                        style={{ width: '100%', border: `1.5px solid ${GREEN}`, borderRadius: 6, padding: '10px 12px', fontFamily: "'Trebuchet MS', sans-serif", fontSize: 13, color: 'var(--text)', background: 'var(--bg2)', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
                      />
                    </div>
                  ))}
                  {/* ── Care Plan & Training Tables ─────────────── */}
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: TEAL, marginBottom: 10, marginTop: 20, paddingBottom: 6, borderBottom: `2px solid ${TEAL}` }}>
                    Section 2 & 3 — Care Plan Tables
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, fontStyle: 'italic' }}>
                    AI-generated rows — edit any cell before generating the document. Use + Add Row for additional entries.
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      2.1  Skilled Nursing Goals & Interventions
                      <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                    </div>
                    <TableSec
                      columns={['Clinical Domain','Measurable Goal','Skilled Intervention & Actions','Frequency / Target']}
                      data={aiSkilledGoals}
                      onChange={updTable(setAiSkilledGoals)}
                      addRow={addRow(setAiSkilledGoals)}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      2.2  Unskilled Care Goals & Tasks
                      <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                    </div>
                    <TableSec
                      columns={['Clinical Domain','Measurable Goal','Unskilled Tasks & Actions','Frequency / Target']}
                      data={aiUnskilledGoals}
                      onChange={updTable(setAiUnskilledGoals)}
                      addRow={addRow(setAiUnskilledGoals)}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      3.1  Competency Audit
                      <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                    </div>
                    <TableSec
                      columns={['Required Care Task','Clinical Risk / Justification','Caregiver Status (Able / Needs Training)','Status (Completed / Pending)']}
                      data={aiCompetencyAudit}
                      onChange={updTable(setAiCompetencyAudit)}
                      addRow={addRow(setAiCompetencyAudit)}
                    />
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontFamily: "'Trebuchet MS', sans-serif", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      3.2  Caregiver Teaching Plan
                      <span style={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>✎ AI — edit as needed</span>
                    </div>
                    <TableSec
                      columns={['Teaching Goal / Objective','Teaching Method & Content','Completion Date','Status (Completed / Pending)']}
                      data={aiTeachingPlan}
                      onChange={updTable(setAiTeachingPlan)}
                      addRow={addRow(setAiTeachingPlan)}
                    />
                  </div>

                  {docError && (
                    <div style={{ background: 'var(--danger-bg)', border: '1.5px solid var(--danger)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>
                      ⚠ {docError}
                    </div>
                  )}

                  {docDriveUrl && (
                    <div style={{ background: 'var(--success-bg)', border: `1px solid ${GREEN}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12 }}>
                      ✅ <strong>Saved to Google Drive:</strong>{' '}
                      <a href={docDriveUrl} target="_blank" rel="noopener noreferrer" style={{ color: GREEN }}>
                        Open document
                      </a>
                      {docVersion != null && (
                        <div style={{ marginTop: 4, color: 'var(--text2)', fontSize: 11 }}>
                          Last generated: v{docVersion}
                          {docVersionDate ? ` · ${new Date(docVersionDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                        </div>
                      )}
                    </div>
                  )}

                  <button onClick={handleGenerateDoc} disabled={docGenerating}
                    style={{
                      width: '100%', padding: '14px 0', borderRadius: 8, fontWeight: 700, fontSize: 14,
                      cursor: docGenerating ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif",
                      background: docGenerating ? 'var(--border2)' : docDriveUrl ? '#fff' : GREEN,
                      color: docGenerating ? '#fff' : docDriveUrl ? GREEN : '#fff',
                      border: docDriveUrl && !docGenerating ? `1.5px solid ${GREEN}` : 'none',
                    }}>
                    {docGenerating ? '⏳ Generating document…' : docDriveUrl ? '🔄 Regenerate Document' : '📄 Confirm Narratives & Generate Document'}
                  </button>
                </div>
              )}

              <div style={{ marginTop: 16, fontSize: 11, color: 'var(--border2)', textAlign: 'center' }}>
                AHFC GAPP Unified Assessment v3.0 · Valid 1 year from NA Date · Renew 30–60 days prior per GAPP Policy Manual
              </div>
            </div>
          </div>
        )}

        {/* Prev / Next */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <button onClick={() => setActiveSection(s => Math.max(0, s - 1))} disabled={activeSection === 0}
            style={{ padding: '10px 24px', borderRadius: 7, border: `1.5px solid ${BORDER}`, background: '#fff', color: activeSection === 0 ? '#bbb' : NAVY, fontWeight: 700, fontSize: 13, cursor: activeSection === 0 ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif" }}>
            ← Previous
          </button>
          <button onClick={() => setActiveSection(s => Math.min(sections.length - 1, s + 1))} disabled={activeSection === sections.length - 1}
            style={{ padding: '10px 24px', borderRadius: 7, border: 'none', background: activeSection === sections.length - 1 ? '#bbb' : NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: activeSection === sections.length - 1 ? 'default' : 'pointer', fontFamily: "'Trebuchet MS', sans-serif" }}>
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
